import React, { useState, useEffect } from 'react';
import { ChevronDown, Save, Users, IndianRupee, CheckCircle, Layers } from 'lucide-react';
import { wagesAPI, attendanceAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useDropdownData } from '../utils/useDropdownData';
import { useAuth, useActiveEstId } from './AuthContext';
import EstablishmentCard from './EstablishmentCard';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

const VariablePay = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use custom hook for dropdown data
  const {
    establishments,
    employees,
    loading,
    fetchEstablishments,
    fetchEmployeesByEstablishment,
    clearEmployees
  } = useDropdownData();

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEstablishment(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch establishments on component mount (SuperAdmin only)
  useEffect(() => {
    if (lockedEstId) return;
    fetchEstablishments();
  }, [fetchEstablishments, lockedEstId]);

  // Auto-select first establishment when establishments load
  useEffect(() => {
    if (establishments.length > 0 && !selectedEstablishment) {
      setSelectedEstablishment(establishments[0].id.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishments]);

  const sections = [
    { id: 'bonus', name: 'Bonus Payment' },
    { id: 'commission', name: 'Commission' },
    { id: 'overtime', name: 'Overtime Pay' },
    { id: 'allowance', name: 'Allowances' }
  ];

  const sectionFields = {
    'bonus': [
      { name: 'bonusType', label: 'Bonus Type', type: 'select', options: ['Performance Bonus', 'Quarterly Bonus', 'Annual Bonus', 'Signing Bonus', 'Retention Bonus', 'Holiday Bonus'], defaultValue: 'Performance Bonus' },
      { name: 'bonusAmount', label: 'Bonus Amount ($)', type: 'number', defaultValue: '' },
      { name: 'bonusMonth', label: 'Payment Month', type: 'select', options: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'], defaultValue: 'January' },
      { name: 'bonusYear', label: 'Payment Year', type: 'select', options: ['2025', '2024', '2023'], defaultValue: '2025' },
      { name: 'bonusReason', label: 'Reason/Description', type: 'textarea', defaultValue: '' },
      { name: 'approvalStatus', label: 'Approval Status', type: 'select', options: ['Pending', 'Approved', 'Paid'], defaultValue: 'Pending' }
    ],
    'commission': [
      { name: 'commissionType', label: 'Commission Type', type: 'select', options: ['Sales Commission', 'Referral Commission', 'Deal Commission'], defaultValue: 'Sales Commission' },
      { name: 'commissionAmount', label: 'Commission Amount ($)', type: 'number', defaultValue: '' },
      { name: 'commissionRate', label: 'Commission Rate (%)', type: 'number', defaultValue: '' },
      { name: 'salesAmount', label: 'Total Sales Amount ($)', type: 'number', defaultValue: '' },
      { name: 'commissionMonth', label: 'Commission Period', type: 'select', options: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'], defaultValue: 'January' },
      { name: 'commissionNotes', label: 'Notes', type: 'textarea', defaultValue: '' }
    ],
    'overtime': [
      { name: 'overtimeHours', label: 'Overtime Hours', type: 'number', defaultValue: '' },
      { name: 'overtimeRate', label: 'Hourly Rate ($)', type: 'number', defaultValue: '' },
      { name: 'overtimeMultiplier', label: 'Overtime Multiplier', type: 'select', options: ['1.5x', '2.0x', '2.5x'], defaultValue: '1.5x' },
      { name: 'overtimeTotal', label: 'Total Overtime Pay ($)', type: 'number', defaultValue: '' },
      { name: 'overtimeDate', label: 'Overtime Date', type: 'date', defaultValue: '' },
      { name: 'overtimeReason', label: 'Reason', type: 'textarea', defaultValue: '' }
    ],
    'allowance': [
      { name: 'allowanceType', label: 'Allowance Type', type: 'select', options: ['Travel Allowance', 'Housing Allowance', 'Food Allowance', 'Transportation', 'Mobile Phone', 'Internet', 'Education', 'Other'], defaultValue: 'Travel Allowance' },
      { name: 'allowanceAmount', label: 'Allowance Amount ($)', type: 'number', defaultValue: '' },
      { name: 'allowanceFrequency', label: 'Frequency', type: 'select', options: ['One-time', 'Monthly', 'Quarterly', 'Annually'], defaultValue: 'Monthly' },
      { name: 'allowanceStartDate', label: 'Start Date', type: 'date', defaultValue: '' },
      { name: 'allowanceEndDate', label: 'End Date (Optional)', type: 'date', defaultValue: '' },
      { name: 'allowanceDescription', label: 'Description', type: 'textarea', defaultValue: '' }
    ]
  };

  const handleEstablishmentChange = (e) => {
    const establishmentId = e.target.value;
    setSelectedEstablishment(establishmentId);
    setSelectedEmployee('');
    setSelectedSection('');
    setFormData({});

    // Fetch employees for selected establishment
    if (establishmentId) {
      fetchEmployeesByEstablishment(establishmentId);
    } else {
      clearEmployees();
    }
  };

  const handleEmployeeChange = (e) => {
    setSelectedEmployee(e.target.value);
    setSelectedSection('');
    setFormData({});
  };

  const handleSectionChange = async (e) => {
    const section = e.target.value;
    setSelectedSection(section);

    const defaults = {};
    sectionFields[section].forEach(field => {
      defaults[field.name] = field.defaultValue;
    });

    // If overtime section is selected and employee is selected, fetch overtime data
    if (section === 'overtime' && selectedEmployee) {
      try {
        const summary = await attendanceAPI.getOvertimeSummary(selectedEmployee);
        // Pre-fill the overtime fields with calculated data
        defaults.overtimeHours = summary.totalHours?.toString() || '';
        defaults.overtimeRate = summary.avgHourlyRate?.toString() || '';
        defaults.overtimeTotal = summary.totalAmount?.toString() || '';
      } catch (error) {
        console.error('Error fetching overtime summary:', error);
        // Continue with default values if fetch fails
      }
    }

    setFormData(defaults);
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async () => {
    if (!selectedEstablishment || !selectedEmployee || !selectedSection) {
      alert('Please select organization, employee, and earning type');
      return;
    }

    setIsSubmitting(true);

    try {
      const variablePayData = {
        establishmentId: parseInt(selectedEstablishment) || 1,
        selectionType: 'employee',
        employeeId: selectedEmployee,
        department: null,
        earningType: selectedSection,
        amount: parseFloat(formData.amount) || 0,
        paymentDate: formData.paymentDate || new Date().toISOString().split('T')[0],
        description: formData.description || '',
        month: formData.month || null,
        performanceRating: formData.performanceRating || null,
        salesAmount: formData.salesAmount ? parseFloat(formData.salesAmount) : null,
        commissionRate: formData.commissionRate ? parseFloat(formData.commissionRate) : null,
        overtimeHours: formData.overtimeHours ? parseFloat(formData.overtimeHours) : null,
        overtimeRate: formData.overtimeRate ? parseFloat(formData.overtimeRate) : null,
        allowanceType: formData.allowanceType || null
      };

      await wagesAPI.addVariablePay(variablePayData);
      notifySavedAndReload('✓ Variable pay added successfully!');

      // Reset form
      setFormData({});
      setSelectedSection('');
    } catch (error) {
      console.error('Error adding variable pay:', error);
      alert('❌ Error adding variable pay: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="update-employee-container">
      <div className="update-employee-wrapper">
        <div className="update-employee-header">
          <h2 className="update-employee-title">Variable Pay - Additional Earnings</h2>
          <p className="update-employee-subtitle">Manage bonuses, commissions, overtime, and allowances</p>
        </div>

        {/* Establishment Selector */}
        {!lockedEstId && (
          <EstablishmentCard
            establishments={establishments}
            value={selectedEstablishment}
            onChange={handleEstablishmentChange}
            loading={loading.establishments}
          />
        )}

        {/* Selection Filters */}
        <div className="attendance-grid-filters">
          {/* Employee Dropdown */}
          {selectedEstablishment && (
            <div className="attendance-filter-group animate-fadeIn">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper employee">
                  <Users size={15} />
                </span>
                Employee
              </label>
              <select
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                className="attendance-filter-select"
                disabled={loading.employees}
              >
                <option value="">
                  {loading.employees ? 'Loading...' : 'Select Employee'}
                </option>
                {employees.map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {emp.first_name} {emp.last_name} ({emp.employee_id}) - {emp.department || 'N/A'}
                  </option>
                ))}
                {!loading.employees && employees.length === 0 && (
                  <option value="" disabled>No employees found</option>
                )}
              </select>
            </div>
          )}

          {/* Section Dropdown */}
          {selectedEmployee && (
            <div className="attendance-filter-group animate-fadeIn">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper type">
                  <Layers size={15} />
                </span>
                Earning Type
              </label>
              <select
                value={selectedSection}
                onChange={handleSectionChange}
                className="attendance-filter-select"
              >
                <option value="">Choose Earning Type</option>
                {sections.map(section => (
                  <option key={section.id} value={section.id}>{section.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Form Fields */}
        {selectedSection && (
          <div className="update-employee-form-card animate-fadeIn">
            <div className="update-employee-section-header">
              <h3 className="update-employee-section-title">
                {sections.find(s => s.id === selectedSection)?.name}
              </h3>
              <div className="update-employee-employee-badge">
                {employees.find(e => e.employee_id === selectedEmployee)?.first_name} {employees.find(e => e.employee_id === selectedEmployee)?.last_name}
              </div>
            </div>

            <div className="update-employee-form-grid">
              {sectionFields[selectedSection].map((field) => (
                <div key={field.name} className={field.type === 'textarea' ? 'full-width' : ''}>
                  <label className="update-employee-field-label">
                    {field.label}
                  </label>
                  {field.type === 'select' ? (
                    <select
                      value={formData[field.name] || ''}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      disabled={field.disabled}
                      className="update-employee-select"
                    >
                      {field.options.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      value={formData[field.name] || ''}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      disabled={field.disabled}
                      className="update-employee-input"
                      rows="3"
                    />
                  ) : (
                    <input
                      type={field.type}
                      value={formData[field.name] || ''}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      disabled={field.disabled}
                      className="update-employee-input"
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="update-employee-submit-section">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="update-employee-btn-submit"
              >
                {isSubmitting ? (
                  <>
                    <div className="update-employee-spinner"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Add Variable Pay
                  </>
                )}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VariablePay;
